import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { Phone, WhatsappLogo, MapPin, EnvelopeSimple, Clock } from "@phosphor-icons/react";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL, EMAIL, ADDRESS_LINE1, ADDRESS_LINE2, MAPS_URL } from "@/lib/contact";
import { CITY_LANDINGS } from "@/data/cityLanding";

export default function Footer() {
  const { t } = useI18n();
  const services = ["svc_airport", "svc_city", "svc_hourly", "svc_business", "svc_events", "svc_wedding", "svc_longdistance", "svc_medical"];

  return (
    <footer className="bg-[#0B1533] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <span className="inline-flex bg-white rounded-xl px-4 py-2.5">
              <img src="/airport-transfer-24-logo.png" alt="Airport Transfer Zurich" className="h-9 w-auto" />
            </span>
            <p className="font-body text-sm text-white/60 max-w-xs leading-relaxed mt-5">{t("footer_tag")}</p>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-5 bg-[#25D366] text-white font-body font-semibold text-sm rounded-full px-5 py-2.5 hover:opacity-90 transition-opacity duration-200">
              <WhatsappLogo size={18} weight="fill" /> {t("whatsapp")}
            </a>
          </div>

          <div>
            <p className="font-heading font-semibold text-sm text-white/50 uppercase tracking-wider mb-4">{t("footer_services")}</p>
            <ul className="flex flex-col gap-2.5">
              {services.map((s) => (
                <li key={s}><Link to="/#services" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t(s)}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-heading font-semibold text-sm text-white/50 uppercase tracking-wider mb-4">{t("footer_company")}</p>
            <ul className="flex flex-col gap-2.5">
              <li><Link to="/" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_book")}</Link></li>
              <li><Link to="/destinations" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_destinations")}</Link></li>
              <li><Link to="/strecken" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_routes")}</Link></li>
              <li><Link to="/preise" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_prices")}</Link></li>
              <li><Link to="/regionen" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_regions")}</Link></li>
              <li><Link to="/ausfluege" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_attractions")}</Link></li>
              <li><Link to="/events" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_events")}</Link></li>
              <li><Link to="/blog" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_blog")}</Link></li>
              <li><Link to="/faq" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_faq")}</Link></li>
              <li><Link to="/contact" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_contact")}</Link></li>
              <li><Link to="/track" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_track")}</Link></li>
              <li><Link to="/admin/login" className="font-body text-sm text-white/70 hover:text-white transition-colors duration-200">{t("nav_admin")}</Link></li>
            </ul>
          </div>

          <div>
            <p className="font-heading font-semibold text-sm text-white/50 uppercase tracking-wider mb-4">{t("footer_contact")}</p>
            <ul className="flex flex-col gap-3.5">
              <li><a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2.5 font-body text-sm text-white/80 hover:text-white transition-colors duration-200"><Phone size={16} weight="fill" className="text-[#EF5A1E] shrink-0" /> {PHONE_DISPLAY}</a></li>
              <li><a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 font-body text-sm text-white/80 hover:text-white transition-colors duration-200"><WhatsappLogo size={16} weight="fill" className="text-[#25D366] shrink-0" /> {PHONE_DISPLAY}</a></li>
              <li><a href={`mailto:${EMAIL}`} className="flex items-center gap-2.5 font-body text-sm text-white/80 hover:text-white transition-colors duration-200"><EnvelopeSimple size={16} className="text-white/60 shrink-0" /> {EMAIL}</a></li>
              <li><a href={MAPS_URL} target="_blank" rel="noreferrer" className="flex items-start gap-2.5 font-body text-sm text-white/80 hover:text-white transition-colors duration-200"><MapPin size={16} weight="fill" className="text-white/60 shrink-0 mt-0.5" /> <span>{ADDRESS_LINE1}<br />{ADDRESS_LINE2}</span></a></li>
              <li className="flex items-center gap-2.5 font-body text-sm text-white/60"><Clock size={16} className="shrink-0" /> {t("hours_value")}</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8">
          <p className="font-heading font-semibold text-sm text-white/50 uppercase tracking-wider mb-4">{t("footer_popular_transfers")}</p>
          <div className="flex flex-wrap gap-2.5" data-testid="footer-city-landings">
            {CITY_LANDINGS.map((c) => (
              <Link key={c.slug} to={`/airport-transfer-${c.slug}`} className="font-body text-xs text-white/70 hover:text-white bg-white/5 hover:bg-white/10 rounded-full px-3.5 py-1.5 transition-colors duration-200" data-testid={`footer-landing-${c.slug}`}>
                Airport Transfer {c.name.en}
              </Link>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col sm:flex-row justify-between gap-2">
          <p className="font-body text-xs text-white/40">© 2026 Airport Transfer Zurich · {ADDRESS_LINE1}, {ADDRESS_LINE2}</p>
          <p className="font-body text-xs text-white/40" data-testid="footer-handelsregister">Handelsregister-Nr.: CH-020.4.082.636-0</p>
        </div>
      </div>
    </footer>
  );
}
